"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { FeatureIcon, FeatureIconType } from "@/components/ui/logo";
import { BRAND_NAME } from "@/lib/config/brand";
import { healthCheckCtaPath } from "@/lib/seo/fix-page-ctas";

// ============ NAVIGATION DATA ============

interface NavLink {
  label: string;
  href: string;
  description?: string;
  icon?: FeatureIconType;
}

interface NavSection {
  title: string;
  links: NavLink[];
}

interface NavDropdown {
  label: string;
  sections: NavSection[];
  featured?: {
    title: string;
    description: string;
    href: string;
    badge?: string;
  };
}

type NavItem = { label: string; href: string } | NavDropdown;

function isDropdown(item: NavItem): item is NavDropdown {
  return "sections" in item;
}

/**
 * Routes that get the stripped header: logo, Sign In, nothing else. These are
 * landing pages whose whole job is one action on the page; thirteen other
 * links in the header (four dropdowns, two CTAs that lead back to this same
 * page) were exits competing with the first tap. `usePathname` is safe here
 * (it does not suspend the way `useSearchParams` does).
 */
const FOCUSED_ROUTES = new Set(["/free-health-check"]);

const NAV_ITEMS: NavItem[] = [
  {
    label: "Platforms",
    sections: [
      {
        title: "Health Checks",
        links: [
          {
            label: "Salesforce",
            href: "/salesforce-health-check",
            description: "200+ automated checks for Salesforce orgs",
          },
          {
            label: "ServiceNow",
            href: "/servicenow-health-check",
            description: "Instance health and configuration audits",
          },
          {
            label: "HubSpot",
            href: "/hubspot-health-check",
            description: "CRM health and data quality checks",
          },
          {
            label: "Splunk",
            href: "/splunk-health-check",
            description: "Search performance and license optimization",
          },
        ],
      },
      {
        title: "Security Audits",
        links: [
          {
            label: "Salesforce Security",
            href: "/salesforce-security-audit",
            description: "Permission gaps and exposure risks",
          },
          {
            label: "Cross-Platform",
            href: "/saas-security-audit",
            description: "Unified security across all platforms",
          },
        ],
      },
    ],
    featured: {
      title: "All Platforms",
      description: "See all supported SaaS platforms and integrations",
      href: "/supported-platforms",
      badge: "4 Platforms",
    },
  },
  {
    label: "Features",
    sections: [
      {
        title: "Core Capabilities",
        links: [
          {
            label: "AI Consultant",
            href: "/features/ai-consultant",
            description: "24/7 AI expert for platform questions",
            icon: "ai",
          },
          {
            label: "Health Check",
            href: "/features/health-check",
            description: "Automated platform health monitoring",
            icon: "health",
          },
          {
            label: "Security Scanner",
            href: "/features/security-scanner",
            description: "Find vulnerabilities before attackers",
            icon: "health",
          },
          {
            label: "Deployment Automation",
            href: "/features/deployment-automation",
            description: "Safe deployments with rollback",
            icon: "deploy",
          },
          {
            label: "Multi-SaaS Integration",
            href: "/features/multi-saas-integration",
            description: "Unified view across platforms",
            icon: "data",
          },
        ],
      },
    ],
    featured: {
      title: "All Features",
      description: `Explore the complete ${BRAND_NAME} platform`,
      href: "/features",
      badge: "5 Features",
    },
  },
  {
    label: "Solutions",
    sections: [
      {
        title: "By Role",
        links: [
          {
            label: "Salesforce Admins",
            href: "/use-cases/salesforce-admin",
            description: "Automate the tedious stuff",
          },
          {
            label: "IT Executives",
            href: "/use-cases/it-executive",
            description: "Cut costs, gain visibility",
          },
          {
            label: "SMB Owners",
            href: "/use-cases/smb-owner",
            description: "Expert help without the cost",
          },
          {
            label: "Accidental Admins",
            href: "/use-cases/accidental-admin",
            description: "AI guidance for non-experts",
          },
          {
            label: "Operations Leaders",
            href: "/use-cases/operations-leader",
            description: "Streamline platform operations",
          },
        ],
      },
      {
        title: "vs Alternatives",
        links: [
          {
            label: "vs Hiring Consultants",
            href: "/vs/hiring-consultant",
            description: "Save 60-80% on consulting costs",
          },
          {
            label: "vs DIY Admin",
            href: "/vs/diy-admin",
            description: "From manual to automated",
          },
          {
            label: "vs Managed Services",
            href: "/vs/managed-services",
            description: "More control, less overhead",
          },
          {
            label: "vs Salesforce Optimizer",
            href: "/vs/salesforce-optimizer",
            description: "Beyond basic recommendations",
          },
        ],
      },
    ],
  },
  {
    label: "Resources",
    sections: [
      {
        title: "Learn",
        links: [
          {
            label: "Documentation",
            href: "/docs",
            description: "Guides, tutorials, and API docs",
          },
          {
            label: "Getting Started",
            href: "/docs/getting-started",
            description: "Set up in under 5 minutes",
          },
          {
            label: "Health Check Guide",
            href: "/docs/health-check-guide",
            description: "Understanding your health score",
          },
          {
            label: "FAQ",
            href: "/docs/faq",
            description: "Common questions answered",
          },
        ],
      },
      {
        title: "Tools",
        links: [
          {
            label: "Health Check Report Assessment",
            href: "/fix-my-health-check",
            description: "Free AI analysis of your health check report",
          },
          {
            label: "ROI Calculator",
            href: "/calculator",
            description: "Calculate your potential savings",
          },
          {
            label: "Insights Blog",
            href: "/insights",
            description: "Tips, trends, and best practices",
          },
          {
            label: "Glossary",
            href: "/docs/glossary",
            description: "SaaS terminology explained",
          },
        ],
      },
    ],
  },
  { label: "Managed Operations", href: "/managed-operations" },
  { label: "Pricing", href: "/pricing" },
];

// ============ DROPDOWN COMPONENT ============

function NavDropdownMenu({
  item,
  isOpen,
  onClose,
}: {
  item: NavDropdown;
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.96 }}
          transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="absolute top-full left-1/2 -translate-x-1/2 pt-4 z-50"
        >
          <div
            className="rounded-2xl border border-slate-200 bg-white backdrop-blur-sm shadow-[0_10px_40px_rgba(0,0,0,0.1)] overflow-hidden"
            style={{ minWidth: "520px" }}
          >
            <div className="flex">
              {/* Main sections */}
              <div className="flex-1 p-4 grid grid-cols-2 gap-6">
                {item.sections.map((section) => (
                  <div key={section.title}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-2">
                      {section.title}
                    </p>
                    <ul className="space-y-1">
                      {section.links.map((link) => (
                        <li key={link.href}>
                          <Link
                            href={link.href}
                            onClick={onClose}
                            className="flex items-start gap-3 px-2 py-2 rounded-lg transition-colors hover:bg-slate-50 group"
                          >
                            {link.icon && (
                              <div className="mt-0.5 flex-shrink-0">
                                <FeatureIcon feature={link.icon} className="h-8 w-8" />
                              </div>
                            )}
                            <div>
                              <span className="text-sm font-medium text-slate-700 group-hover:text-primary transition-colors">
                                {link.label}
                              </span>
                              {link.description && (
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                  {link.description}
                                </p>
                              )}
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Featured card */}
              {item.featured && (
                <div className="w-48 bg-slate-50 border-l border-slate-200 p-4 flex flex-col justify-center">
                  <Link
                    href={item.featured.href}
                    onClick={onClose}
                    className="block p-3 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 hover:border-primary/40 transition-colors group"
                  >
                    {item.featured.badge && (
                      <span className="inline-block text-[10px] font-semibold text-primary bg-primary/8 px-2 py-0.5 rounded-full mb-2">
                        {item.featured.badge}
                      </span>
                    )}
                    <p className="text-sm font-semibold text-slate-700 group-hover:text-primary transition-colors">
                      {item.featured.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {item.featured.description}
                    </p>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============ MOBILE MENU ============

function MobileMenu({
  isOpen,
  onClose,
  ctaHref,
}: {
  isOpen: boolean;
  onClose: () => void;
  ctaHref: string;
}) {
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="min-[1320px]:hidden mt-2 rounded-2xl border border-slate-200 bg-white backdrop-blur-sm shadow-[0_8px_32px_rgba(0,0,0,0.08)] max-h-[70vh] overflow-y-auto"
        >
          <nav className="p-3">
            {NAV_ITEMS.map((item) =>
              isDropdown(item) ? (
                <div key={item.label} className="mb-1">
                  <button
                    onClick={() =>
                      setExpandedItem(
                        expandedItem === item.label ? null : item.label
                      )
                    }
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 rounded-xl transition-all hover:text-slate-900 hover:bg-slate-100"
                  >
                    {item.label}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        expandedItem === item.label ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <AnimatePresence>
                    {expandedItem === item.label && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 pb-2">
                          {item.sections.map((section) => (
                            <div key={section.title} className="mb-3">
                              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 px-4">
                                {section.title}
                              </p>
                              {section.links.map((link) => (
                                <Link
                                  key={link.href}
                                  href={link.href}
                                  onClick={onClose}
                                  className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-colors"
                                >
                                  {link.icon && (
                                    <FeatureIcon feature={link.icon} className="h-6 w-6" />
                                  )}
                                  {link.label}
                                </Link>
                              ))}
                            </div>
                          ))}
                          {item.featured && (
                            <Link
                              href={item.featured.href}
                              onClick={onClose}
                              className="block mx-2 mt-2 p-3 text-sm font-medium text-primary bg-primary/8 rounded-xl hover:bg-primary hover:text-primary-foreground transition-colors"
                            >
                              {item.featured.title} →
                            </Link>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={onClose}
                  className="block px-4 py-3 text-sm font-medium text-slate-700 rounded-xl transition-all hover:text-slate-900 hover:bg-slate-100"
                >
                  {item.label}
                </Link>
              )
            )}

            {/* CTAs */}
            <div className="mt-3 pt-3 border-t border-slate-200 flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="justify-start text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-xl"
                asChild
                onClick={onClose}
              >
                <Link href="/auth?tab=login">Sign In</Link>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="justify-start text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-xl"
                asChild
                onClick={onClose}
              >
                <Link href="/auth?tab=signup">Create account</Link>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl border-primary/40 bg-primary/8 text-primary hover:bg-primary hover:text-primary-foreground"
                asChild
                onClick={onClose}
              >
                <Link href="/demo">Book a Demo</Link>
              </Button>
              <Button
                size="sm"
                className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(37,99,235,0.2)]"
                asChild
                onClick={onClose}
              >
                <Link href={ctaHref}>
                  Get Started
                </Link>
              </Button>
            </div>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============ MAIN HEADER ============

export function MarketingHeader() {
  const pathname = usePathname();
  const focused = FOCUSED_ROUTES.has(pathname ?? "");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const handleCta = (event: string) => () => {
    trackEvent({ event, sendInstantly: true });
    setMobileOpen(false);
  };

  return (
    <header className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-7xl">
      <motion.nav
        ref={navRef}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className={`
          relative flex items-center justify-between gap-2 px-3 py-2
          rounded-full border border-slate-200
          bg-white/95 backdrop-blur-sm
          shadow-[0_1px_3px_rgba(0,0,0,0.08),0_4px_16px_rgba(0,0,0,0.04)]
          transition-all duration-300
          ${scrolled ? "bg-white border-slate-200/80" : ""}
        `}
      >
        {/* Logo */}
        <Link
          href="/"
          className="flex shrink-0 items-center gap-2 pl-2"
        >
          <Image
            src="/logos/app-icon-light.png"
            alt={BRAND_NAME}
            width={28}
            height={28}
            className="h-7 w-auto"
          />
          <span className="whitespace-nowrap font-semibold text-slate-900">{BRAND_NAME}</span>
        </Link>

        {focused ? (
          <div className="flex items-center gap-1.5 pr-1">
            <Button
              variant="ghost"
              size="sm"
              className="text-sm text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-full px-3"
              asChild
              onClick={handleCta("click_cta_nav_sign_in")}
            >
              <Link href="/auth?tab=login">Sign In</Link>
            </Button>
          </div>
        ) : (
        <>
        {/* Desktop Navigation */}
        <nav className="hidden min-[1320px]:flex min-w-0 items-center gap-1">
          {NAV_ITEMS.map((item) =>
            isDropdown(item) ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                    activeDropdown === item.label
                      ? "text-slate-900 bg-slate-100"
                      : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  {item.label}
                  <ChevronDown
                    className={`h-3.5 w-3.5 transition-transform ${
                      activeDropdown === item.label ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <NavDropdownMenu
                  item={item}
                  isOpen={activeDropdown === item.label}
                  onClose={() => setActiveDropdown(null)}
                />
              </div>
            ) : (
              <Link
                key={item.href}
                href={item.href}
                className="px-3 py-2 text-sm font-medium text-slate-700 rounded-full transition-all duration-200 hover:text-slate-900 hover:bg-slate-100 whitespace-nowrap"
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden min-[1320px]:flex shrink-0 items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-full px-3"
            asChild
            onClick={handleCta("click_cta_nav_sign_in")}
          >
            <Link href="/auth?tab=login">Sign In</Link>
          </Button>
          {/* Until 2026-10-07 the only /auth link on the public site was
              "Sign In"; a visitor with no account had no direct path to one. */}
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-full px-3"
            asChild
            onClick={handleCta("click_cta_nav_create_account")}
          >
            <Link href="/auth?tab=signup">Create account</Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full border-primary/40 bg-primary/8 px-4 text-primary hover:bg-primary hover:text-primary-foreground"
            asChild
            onClick={handleCta("demo_booking_clicked")}
          >
            <Link href="/demo">Book a Demo</Link>
          </Button>
          <Button
            size="sm"
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-4 shadow-[0_0_20px_rgba(37,99,235,0.2)]"
            asChild
            onClick={handleCta("click_cta_nav_get_started")}
          >
            <Link href={healthCheckCtaPath(pathname)}>Get Started</Link>
          </Button>
        </div>

        {/* Compact bar (below 1320px): primary CTA stays visible beside the menu
            toggle. The full nav needs ~1,260px; below that it overflowed the
            pill and squashed the logo into the first nav item. */}
        <div className="flex shrink-0 items-center gap-1.5 min-[1320px]:hidden">
        <Button
          size="sm"
          className="hidden sm:inline-flex rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-4 shadow-[0_0_20px_rgba(37,99,235,0.2)]"
          asChild
          onClick={handleCta("click_cta_nav_get_started")}
        >
          <Link href={healthCheckCtaPath(pathname)}>Get Started</Link>
        </Button>
        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          className="p-2 text-slate-700 transition-colors hover:text-slate-900 rounded-full hover:bg-slate-100"
          aria-label="Toggle navigation"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        </div>
        </>
        )}
      </motion.nav>

      {/* Mobile Menu */}
      {!focused && <MobileMenu isOpen={mobileOpen} onClose={() => setMobileOpen(false)} ctaHref={healthCheckCtaPath(pathname)} />}
    </header>
  );
}
